import { useProjectLimitsContext } from "../ProjectLimitsContext";
import { useQueryClient } from "@tanstack/react-query";
import { Module } from "@/constants";
import { useCallback, useMemo } from "react";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";

const useProjectLimitQueryInvalidator = () => {
  const { project } = useProjectLimitsContext();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  const isRoleDependent = isProjectRoleDependent(project);

  const invalidateProjectLimits = useCallback(() => {
    const name = isRoleDependent
      ? "projectLimitWithRoles"
      : "projectLimitWithDepartments";
    queryClient.invalidateQueries({
      queryKey: [name, projectId],
      refetchType: "all",
    });
  }, [isRoleDependent, projectId, queryClient]);

  const invalidateProjectLimitsStoreHours = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [Module.ProjectLimits, projectId, "store-hours"],
    });
  }, [projectId, queryClient]);

  const invalidateProjectLimitsPeriodsSummary = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [Module.ProjectLimits, projectId, "periods-summary"],
    });
  }, [projectId, queryClient]);

  const invalidateAll = useCallback(() => {
    invalidateProjectLimits();
    invalidateProjectLimitsStoreHours();
    invalidateProjectLimitsPeriodsSummary();
  }, [
    invalidateProjectLimits,
    invalidateProjectLimitsStoreHours,
    invalidateProjectLimitsPeriodsSummary,
  ]);

  return useMemo(
    () => ({
      invalidateAll,
      invalidateProjectLimits,
      invalidateProjectLimitsStoreHours,
      invalidateProjectLimitsPeriodsSummary,
    }),
    [
      invalidateAll,
      invalidateProjectLimits,
      invalidateProjectLimitsStoreHours,
      invalidateProjectLimitsPeriodsSummary,
    ],
  );
};

export default useProjectLimitQueryInvalidator;
